import { useState } from "react"
import styled from "styled-components"

import { NoHomeGroups } from "src/components/EmptyState/NoHomeGroups"
import { Pager } from "src/components/Pager/Pager"
import { useUrlPager } from "src/components/Pager/useUrlPager"
import { usePostCreateBuildingClicked } from "src/data/analytics/queries/homegroupAnalyticsQueries"
import {
  useDeleteHomeGroup,
  useFetchHomeGroups,
} from "src/data/homeGroups/queries/homeGroupQueries"
import { THomeGroup } from "src/data/homeGroups/types/homeGroupTypes"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { getAccessLogic } from "src/data/user/logic/accessLogic"
import { useTranslate } from "src/i18n/useTranslate"
import { Routes } from "src/router/routes"
import { useRouter } from "src/router/useRouter"
import { MButton } from "src/ui/Button/MButton"
import ConfirmDialog from "src/ui/Dialog/ConfirmDialog"
import { GridTable } from "src/ui/GridTable/GridTable"
import { MoreButton } from "src/ui/GridTable/MoreButton"
import {
  TableColumn,
  useTableColumns,
} from "src/ui/GridTable/useTableColumns/useTableColumns"
import { Titlebar } from "src/ui/Layout/Titlebar"
import { InternalLink } from "src/ui/Link/InternalLink"
import { MSkeleton } from "src/ui/MSkeleton/MSkeleton"
import { spacing } from "src/ui/spacing"

export function HomeGroups({ type }: { type: THomeGroup["type"] }) {
  const { t, langKeys } = useTranslate()
  const { navigate } = useRouter()

  const { org } = useOrganization()
  const { limit, offset, setOffset } = useUrlPager({
    offsetKey: "buildings_offset",
  })

  const homeGroupType =
    type === "area"
      ? "area,unset" // include homegroups where type is unset under area aka 'Location'
      : type
  const fetchHomeGroups = useFetchHomeGroups({
    orgId: org.id,
    filter: {
      limit,
      offset,
      type: homeGroupType,
    },
  })
  const deleteHomeGroup = useDeleteHomeGroup()
  const homeGroups = fetchHomeGroups.data?.homegroups ?? []
  const [deleteHomeGroupId, setDeleteHomeGroupId] = useState<string>()
  const showEmptyState = homeGroups.length < 1 && !fetchHomeGroups.isLoading

  function getErrorMessage() {
    if (!deleteHomeGroup.isError) {
      return null
    }

    if (
      deleteHomeGroup.error?.response?.data?.error_key === "homegroup_not_empty"
    ) {
      return t(langKeys.organizations_team_delete_homegroup_error_text)
    }

    return t(langKeys.failed_something_went_wrong)
  }

  const canAdminHomeGroups = getAccessLogic({
    role: org.user_role,
  }).hasAdminAccess

  const postCreateBuildingClicked = usePostCreateBuildingClicked()

  function handleCreateHomegroup() {
    if (type === "building") {
      postCreateBuildingClicked.mutate()
      navigate(Routes.CreateHomeGroupBuilding.location())
    } else {
      navigate(Routes.CreateHomeGroupLocation.location())
    }
  }

  function handleDeleteHomeGroupClicked(hgId: string) {
    setDeleteHomeGroupId(hgId)
  }

  function handleDeleteHomeGroupConfirmed(hgId: string | undefined) {
    hgId &&
      deleteHomeGroup.mutate(
        { orgId: org.id, homeGroupId: hgId },
        {
          onSuccess: () => setDeleteHomeGroupId(undefined),
        }
      )
  }

  const tableColumns: TableColumn<THomeGroup>[] = [
    {
      value: "unit",
      columnWidth: "auto",
      label:
        type === "building"
          ? t(langKeys.home_group_building)
          : t(langKeys.organizations_home_group),
      renderCell: (data) => (
        <InternalLink to={Routes.HomeGroupDetails.path(data.id)}>
          {data.name}
        </InternalLink>
      ),
    },
    {
      value: "count",
      label: "",
      renderCell: (data) => (
        <span>
          {t(langKeys.home_count, {
            count: data.home_counts.private + data.home_counts.communal,
          })}
        </span>
      ),
    },
    {
      value: "more",
      label: "",
      renderCell: (data) => (
        <div>
          {canAdminHomeGroups && (
            <MoreButton
              onDelete={() => handleDeleteHomeGroupClicked(data.id)}
            />
          )}
        </div>
      ),
    },
  ]

  const { headerElements, rows, templateColumns } = useTableColumns({
    columns: tableColumns,
    data: homeGroups,
  })

  if (fetchHomeGroups.isLoading) {
    return (
      <div>
        <MSkeleton height={30} />
        <MSkeleton height={30} />
        <MSkeleton height={30} />
      </div>
    )
  }

  if (showEmptyState) {
    return <NoHomeGroups type={type} />
  }

  return (
    <div>
      <Titlebar
        title={
          type === "building"
            ? t(langKeys.home_group_building, { count: 10 })
            : t(langKeys.organizations_home_group_plural)
        }
        description={
          type === "building" ? (
            <>
              {t(langKeys.home_group_building_description)}{" "}
              <InternalLink to={Routes.HomeGroupLocations.location()}>
                {t(langKeys.home_group_location_goto)}
              </InternalLink>
            </>
          ) : (
            <>
              {t(langKeys.home_group_location_description)}{" "}
              <InternalLink to={Routes.HomeGroupBuildings.location()}>
                {t(langKeys.home_group_building_goto)}
              </InternalLink>
            </>
          )
        }
        titleVariant="heading2"
        actionBar={
          canAdminHomeGroups && (
            <MButton onClick={handleCreateHomegroup}>
              {type === "building"
                ? t(langKeys.home_group_building_create)
                : t(langKeys.home_group_location_create)}
            </MButton>
          )
        }
        size="unset"
      />
      <GridTable
        header={headerElements}
        rows={rows ?? []}
        templateColumns={templateColumns}
        useFallbackResponsiveMode
      />
      <Wrapper>
        <Pager
          limit={limit}
          offset={offset}
          setOffset={setOffset}
          totalCount={fetchHomeGroups.data?.paging.total_count}
        />
      </Wrapper>

      <ConfirmDialog
        title={t(langKeys.organizations_delete_team_confirm_title)}
        description={t(langKeys.organizations_home_groups_remove_confirm_body, {
          homegroup:
            homeGroups.find((hg) => hg.id === deleteHomeGroupId)?.name ?? "",
        })}
        loading={deleteHomeGroup.isLoading}
        open={!!deleteHomeGroupId}
        onClose={() => {
          setDeleteHomeGroupId(undefined)
        }}
        confirmButtonProps={{
          color: "destructive",
        }}
        confirmLabel={t(langKeys.delete)}
        onConfirm={() => handleDeleteHomeGroupConfirmed(deleteHomeGroupId)}
        error={getErrorMessage()}
      />
    </div>
  )
}

const Wrapper = styled.div`
  margin-top: ${spacing.XL};
`
